import type { AxiosInstance } from 'axios';

import {
  TOKEN_LOCAL_STORAGE_KEY,
  VERIFIER_SESSION_STORAGE_KEY,
} from './constants';
import {
  generateCodeChallenge,
  generateCodeVerifier,
  randomState,
} from './pkce';
import { LocalStorage } from './storage';
import type { IOAuth2Config, IUserProfile } from './types';

export const START_LOGIN_KEY = 'startLogin';

/**
 * Initiates OAuth2 authorization code flow with PKCE.
 *
 * Flow:
 * 1. Generate cryptographically secure code_verifier and code_challenge
 * 2. Store code_verifier in session storage for later use
 * 3. Redirect user to authorization server with PKCE parameters
 *
 * Security: Uses PKCE to prevent authorization code interception attacks
 */
export const startLogin = async (config: IOAuth2Config) => {
  const codeVerifier = generateCodeVerifier(64);
  LocalStorage.set(VERIFIER_SESSION_STORAGE_KEY, codeVerifier);

  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = randomState();

  const authUrl = new URL(config.authUrl);
  authUrl.searchParams.set('response_type', config.responseType);
  authUrl.searchParams.set('client_id', config.clientId);
  authUrl.searchParams.set('redirect_uri', config.redirectUri);
  authUrl.searchParams.set('scope', config.scope);
  authUrl.searchParams.set('state', state);
  authUrl.searchParams.set('code_challenge', codeChallenge);
  authUrl.searchParams.set('code_challenge_method', config.codeChallengeMethod);

  window.location.href = authUrl.toString();
};

export const COMPLETE_LOGIN_KEY = 'completeLogin';

/**
 * Completes OAuth2 authorization code flow by exchanging authorization code for tokens.
 *
 * Flow:
 * 1. Extract authorization code and error parameters from URL
 * 2. Retrieve stored code_verifier from session storage
 * 3. Exchange code + verifier for access token via token endpoint
 * 4. Store token securely and clean up session storage
 *
 * @returns Promise resolving to access token object
 * @throws Error if authorization failed, code missing, or verifier not found
 */
export const completeLoginFromCallback = async (
  config: IOAuth2Config,
  axiosInstance: AxiosInstance,
  setToken: (token: string | undefined) => void
): Promise<{
  accessToken: string;
  refreshToken?: string;
}> => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const codeVerifier = LocalStorage.get(VERIFIER_SESSION_STORAGE_KEY);

  const err = params.get('error');
  if (err) {
    const desc = params.get('error_description') ?? err;
    throw new Error(desc);
  }

  if (!code) {
    throw new Error(
      'No authorization code found. Please try logging in again.'
    );
  }

  if (!codeVerifier) {
    throw new Error('PKCE verifier not found (session expired?)');
  }

  const body = new URLSearchParams({
    grant_type: config.grantType,
    client_id: config.clientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: codeVerifier,
  });

  const { data } = await axiosInstance.post(config.tokenUrl, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    withCredentials: true,
  });

  const accessToken: string | undefined =
    data?.access_token ?? data?.token?.accessToken;
  if (!accessToken) throw new Error('No access_token in token response');

  const refreshToken: string | undefined =
    data?.refresh_token ?? data?.token?.refreshToken;

  LocalStorage.set(TOKEN_LOCAL_STORAGE_KEY, {
    accessToken,
    refreshToken,
  });
  setToken(accessToken);

  LocalStorage.remove(VERIFIER_SESSION_STORAGE_KEY);

  return { accessToken, refreshToken };
};

export const LOGOUT_KEY = 'logout';

/**
 * Performs local logout by clearing stored tokens and resetting axios headers.
 * Note: This only clears local state. For complete logout, call server logout endpoint.
 */
export const logoutLocal = (setToken: (token: string | undefined) => void) => {
  LocalStorage.remove(TOKEN_LOCAL_STORAGE_KEY);
  setToken(undefined);
};

export const SERVER_LOGOUT_KEY = 'serverLogout';

/**
 * Calls the server logout endpoint to invalidate the session.
 * Should be called before performing local logout for complete logout.
 */
export const serverLogout = async (axiosInstance: AxiosInstance) => {
  try {
    await axiosInstance.get('/oauth2/logout');
    return true;
  } catch (error) {
    console.error('Error during server logout:', error);
    return false;
  }
};

export const FETCH_USER_PROFILE_KEY = 'fetchUserProfile';
export const fetchUserProfile = async (
  axiosInstance: AxiosInstance
): Promise<IUserProfile> => {
  const response = await axiosInstance.get('/ims/api/users/my-profile');
  return response.data;
};
